import type {
  Audio,
  Gallery,
  Photo,
  Sight,
  SightIcon,
  Video,
} from "../types";
import type { Locale } from "../../server/src/shared/locale";
import { $devConfig } from "../state/appConfig";

const normalizeApiHost = (value: string | undefined | null): string => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.replace(/\/+$/, "");
};

const getApiHost = (): string => {
  if (typeof window === "undefined") {
    return "";
  }
  const { assistantApiHost } = $devConfig.getState();
  return normalizeApiHost(assistantApiHost);
};

const prefixUrl = (value?: string): string | undefined => {
  if (!value) {
    return value;
  }
  if (!value.startsWith("/")) {
    return value;
  }
  const host = getApiHost();
  return host ? `${host}${value}` : value;
};

const prefixSrcSet = (srcSet?: string): string | undefined => {
  if (!srcSet) {
    return srcSet;
  }
  return srcSet
    .split(",")
    .map((entry) => {
      const trimmed = entry.trim();
      if (!trimmed) {
        return trimmed;
      }
      const [url, descriptor] = trimmed.split(/\s+/, 2);
      const nextUrl = prefixUrl(url) ?? url;
      return descriptor ? `${nextUrl} ${descriptor}` : nextUrl;
    })
    .join(", ");
};

const mapIcon = (icon?: SightIcon): SightIcon | undefined => {
  if (!icon) {
    return icon;
  }
  return {
    ...icon,
    src: prefixUrl(icon.src) ?? icon.src,
    srcSet: prefixSrcSet(icon.srcSet),
  };
};

const mapPhoto = (photo: Photo): Photo => ({
  ...photo,
  src: prefixUrl(photo.src) ?? photo.src,
  thumbnailSrc: prefixUrl(photo.thumbnailSrc),
});

const mapVideo = (video: Video): Video => ({
  ...video,
  src: prefixUrl(video.src) ?? video.src,
  thumbnailSrc: prefixUrl(video.thumbnailSrc),
});

const mapAudio = (audio: Audio): Audio => ({
  ...audio,
  src: prefixUrl(audio.src) ?? audio.src,
});

const mapGallery = (gallery: Gallery): Gallery => ({
  ...gallery,
  items: gallery.items.map((item) => (item.type === "photo" ? mapPhoto(item) : mapVideo(item))),
});

const resolveLocalizedString = (
  value: unknown,
  locale: Locale
): string | undefined => {
  if (typeof value === "string") {
    return value;
  }
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const fromLocale = record[locale];
  if (typeof fromLocale === "string") {
    return fromLocale;
  }
  const fallback =
    typeof record.en === "string"
      ? record.en
      : typeof record.ar === "string"
        ? record.ar
        : undefined;
  return fallback;
};

export const mapSight = (sight: Sight, locale: Locale): Sight => ({
  ...sight,
  name: resolveLocalizedString(sight.name, locale),
  description:
    typeof sight.description === "string"
      ? sight.description
      : resolveLocalizedString(sight.description, locale),
  images: sight.images?.map(mapPhoto),
  icon: mapIcon(sight.icon),
  details: sight.details?.map((item) => {
    if (!("type" in item)) {
      return mapGallery(item);
    }
    switch (item.type) {
      case "photo":
        return mapPhoto(item);
      case "video":
        return mapVideo(item);
      case "audio":
        return mapAudio(item);
      case "text":
        return typeof item.value === "string"
          ? item
          : { ...item, value: resolveLocalizedString(item.value, locale) ?? "" };
      default:
        return item;
    }
  }),
});
